/**
 * População aproximada por país (ISO-3166 alpha-2), estimativas ~2023
 * (ONU/World Bank, arredondadas). Usada SÓ na coluna "População" da aba Países
 * de `/countries` (admin) e na soma dos selecionados — não é fonte da verdade de
 * nada de negócio, é referência informativa para o super-admin priorizar
 * mercados.
 *
 * Origem: sem issue — Sprint 40 (coluna População da aba Países).
 *
 * Contrato: cobre todos os códigos de `ALL_PLAY_LOCATIONS`
 * (`playStoreCountries.ts`, teste em `countryPopulation.test.ts`). Território
 * sem população permanente (AQ, BV, GS, HM, TF, UM) é `null` explícito — a
 * coluna mostra `—`. KP, SY, MH e PW ficam: não estão na Play, mas podem ter
 * doc em `/config/markets` e aparecer na tabela.
 *
 * Usado por: `features/countries/CountriesTab.tsx`.
 *
 * Histórico:
 *  - 2026-10-13 11:20 +0200 — (#957) territórios da lista da Play (HK, MO, XK,
 *    GG, JE, IM, GI, caribenhos, Oceania) e `null` explícito.
 */
const COUNTRY_POPULATION: Readonly<Record<string, number | null>> = {
  // África
  DZ: 45_400_000, AO: 36_700_000, BJ: 13_700_000, BW: 2_675_000, BF: 23_250_000,
  BI: 13_240_000, CV: 600_000, CM: 28_650_000, CF: 5_740_000, TD: 18_280_000,
  KM: 850_000, CG: 6_100_000, CD: 102_260_000, CI: 28_870_000, DJ: 1_140_000,
  EG: 112_720_000, GQ: 1_710_000, ER: 3_750_000, SZ: 1_210_000, ET: 126_530_000,
  GA: 2_440_000, GM: 2_770_000, GH: 34_120_000, GN: 14_190_000, GW: 2_150_000,
  KE: 55_100_000, LS: 2_330_000, LR: 5_420_000, LY: 6_890_000, MG: 30_330_000,
  MW: 20_930_000, ML: 23_290_000, MR: 4_860_000, MU: 1_300_000, MA: 37_840_000,
  MZ: 33_900_000, NA: 2_600_000, NE: 27_200_000, NG: 223_800_000, RW: 14_090_000,
  ST: 230_000, SN: 17_760_000, SC: 110_000, SL: 8_790_000, SO: 18_140_000,
  ZA: 60_410_000, SS: 11_090_000, SD: 48_110_000, TZ: 67_440_000, TG: 9_050_000,
  TN: 12_460_000, UG: 48_580_000, ZM: 20_570_000, ZW: 16_660_000,
  // América do Norte
  AG: 94_000, BS: 410_000, BB: 281_000, BZ: 410_000, CA: 38_780_000,
  CR: 5_210_000, CU: 11_190_000, DM: 73_000, DO: 11_330_000, SV: 6_360_000,
  GD: 126_000, GT: 17_980_000, HT: 11_720_000, HN: 10_590_000, JM: 2_830_000,
  MX: 128_460_000, NI: 7_050_000, PA: 4_470_000, KN: 48_000, LC: 180_000,
  VC: 104_000, TT: 1_530_000, US: 339_990_000,
  // América do Sul
  AR: 45_770_000, BO: 12_390_000, BR: 216_420_000, CL: 19_630_000, CO: 52_090_000,
  EC: 18_190_000, GY: 810_000, PY: 6_860_000, PE: 34_350_000, SR: 620_000,
  UY: 3_420_000, VE: 28_840_000,
  // Ásia
  AF: 42_240_000, AM: 2_780_000, AZ: 10_410_000, BH: 1_490_000, BD: 172_950_000,
  BT: 790_000, BN: 450_000, KH: 16_940_000, CN: 1_425_670_000, GE: 3_730_000,
  IN: 1_428_630_000, ID: 277_530_000, IR: 89_170_000, IQ: 45_500_000, IL: 9_170_000,
  JP: 123_290_000, JO: 11_330_000, KZ: 19_610_000, KW: 4_310_000, KG: 6_740_000,
  LA: 7_630_000, LB: 5_490_000, MY: 34_310_000, MV: 520_000, MN: 3_450_000,
  MM: 54_580_000, NP: 30_900_000, KP: 26_160_000, OM: 4_640_000, PK: 240_490_000,
  PS: 5_370_000, PH: 117_340_000, QA: 2_720_000, SA: 36_950_000, SG: 6_050_000,
  KR: 51_780_000, LK: 21_890_000, SY: 23_230_000, TW: 23_920_000, TJ: 10_140_000,
  TH: 71_800_000, TL: 1_360_000, TR: 85_820_000, TM: 6_520_000, AE: 9_520_000,
  UZ: 35_160_000, VN: 98_860_000, YE: 34_450_000,
  // Europa
  AL: 2_830_000, AD: 80_000, AT: 8_960_000, BY: 9_500_000, BE: 11_690_000,
  BA: 3_210_000, BG: 6_690_000, HR: 4_010_000, CY: 1_260_000, CZ: 10_610_000,
  DK: 5_910_000, EE: 1_320_000, FI: 5_550_000, FR: 64_760_000, DE: 83_290_000,
  GR: 10_340_000, HU: 10_160_000, IS: 380_000, IE: 5_060_000, IT: 58_870_000,
  LV: 1_830_000, LI: 39_000, LT: 2_720_000, LU: 660_000, MT: 540_000,
  MD: 3_440_000, MC: 36_000, ME: 626_000, NL: 17_620_000, MK: 2_090_000,
  NO: 5_470_000, PL: 41_030_000, PT: 10_250_000, RO: 19_890_000, RU: 144_440_000,
  SM: 33_000, RS: 7_150_000, SK: 5_800_000, SI: 2_120_000, ES: 47_520_000,
  SE: 10_610_000, CH: 8_800_000, UA: 36_740_000, GB: 67_740_000, VA: 800,
  // Oceania
  AU: 26_440_000, FJ: 940_000, KI: 130_000, MH: 42_000, FM: 115_000,
  NR: 12_000, NZ: 5_230_000, PW: 18_000, PG: 10_330_000, WS: 220_000,
  SB: 740_000, TO: 107_000, TV: 11_000, VU: 330_000,
  // #957 — territórios e locais da lista da Play que não estavam no dataset.
  EH: 590_000, IO: 3_000, SH: 5_600,
  AI: 15_900, AW: 106_000, BM: 64_000, BQ: 30_000, CW: 192_000, KY: 69_000,
  MS: 4_400, TC: 46_000, VG: 31_000,
  FK: 3_700,
  HK: 7_500_000, MO: 700_000,
  GG: 64_000, GI: 33_000, IM: 84_000, JE: 103_000, XK: 1_760_000,
  CC: 600, CK: 17_000, CX: 1_700, NF: 2_200, NU: 1_700, PN: 50, TK: 1_900,
  // Sem população permanente (bases científicas/militares, ilhas desabitadas).
  AQ: null, BV: null, GS: null, HM: null, TF: null, UM: null,
};

/** População aproximada do país, ou `null` se o código não estiver no dataset. */
export function countryPopulation(code: string): number | null {
  return COUNTRY_POPULATION[code.toUpperCase()] ?? null;
}

/**
 * O código tem linha no dataset (com número ou `null` explícito)? Distingue
 * "sem população permanente" de "esquecido no dataset" — o teste exige linha
 * para todo local da Play (#957).
 */
export function hasPopulationEntry(code: string): boolean {
  return Object.prototype.hasOwnProperty.call(COUNTRY_POPULATION, code.toUpperCase());
}
